import { memo, useCallback, useMemo, useState } from 'react'
import { useWatch } from 'react-hook-form'
import { type TableComponents, TableVirtuoso } from 'react-virtuoso'

import TableBody from '@material-ui/core/TableBody'
import TableFooter from '@material-ui/core/TableFooter'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { type Theme, makeStyles } from '@material-ui/core/styles'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import classNames from 'classnames'

import FormField from '~/common/components/form-field'
import { MAX_VIRTUALIZED_HEIGHT } from '~/consts'
import { useDimensionHelpers } from '~/products/hooks/useDimensionHelpers'
import {
    type ProductFormSchema,
    type StockUnitFormSchema,
    useProductFormContext,
} from '~/products/hooks/useProductForm'
import { useVirtualizationContainer } from '~/products/hooks/useVirtualizationContainer'
import { createChannelValidator } from '~/products/utils/createChannelValidator'
import { computeStockUnitItemKey } from '~/products/utils/stock-unit'

const DEFAULT_FIELD_WIDTH = 125

export const usePricingStyles = makeStyles<Theme, { fieldWidth: number }>((theme) => ({
    headerRow: {
        backgroundColor: theme.palette.background.paper,
        textAlign: 'left',
    },
    priceFieldColumn: {
        '& > div': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: (props) => props.fieldWidth,
        },
    },
    priceField: {
        height: 35,
    },
    rowPriceField: {
        '& .MuiFormHelperText-root': {
            marginLeft: 0,
            marginRight: 0,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
    },
    priceColumn: {
        maxWidth: (props) => props.fieldWidth,
        textAlign: 'center',
        verticalAlign: 'top',
    },
    firstColumn: {
        width: (props) => props.fieldWidth,
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
    },
    directionIcon: {
        color: theme.palette.grey[500],
    },
    pricingColumnHeader: {
        display: 'flex',
        alignItems: 'center',
    },
    pricingChannelIconURL: {
        width: 20,
        marginRight: 5,
    },
    pricingColumnShopName: {
        fontSize: '0.9rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    skuPlaceholder: {
        color: theme.palette.grey[500],
    },
    skuColumn: {
        maxWidth: '13ch',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
}))

type PriceFieldProps = {
    cpIndex: number
    csuIndex: number
    fieldWidth: number
    channelStockUnits: ProductFormSchema['channel_products'][number]['channel_stock_units']
}

function PriceField({ cpIndex, channelStockUnits, csuIndex, fieldWidth }: PriceFieldProps) {
    const classes = usePricingStyles({ fieldWidth })
    const channelId: number = useWatch({ name: `channel_products.${cpIndex}.shop.channel.id` })
    const channelValidator = useMemo(() => createChannelValidator(channelId), [channelId])
    const currentChannelStockUnit = channelStockUnits.at(csuIndex)

    /**
     * Get all non empty dependencies, excluding itself (because it already validates its own field)
     */
    const nonEmptyDependencies = useMemo(() => {
        return channelStockUnits.reduce((acc, csu, index) => {
            if (parseInt(csu.price) > 0 && index !== csuIndex) {
                acc.push(`channel_products.${cpIndex}.channel_stock_units.${index}.price`)
            }

            return acc
        }, [] as `channel_products.${number}.channel_stock_units.${number}.price`[])
    }, [channelStockUnits, cpIndex, csuIndex])

    const isPromotionPeriod: boolean = useWatch({
        name: `channel_products.${cpIndex}.is_promotion_period`,
    })
    const isDisabled = isPromotionPeriod || currentChannelStockUnit?.placeholder

    if (!currentChannelStockUnit) {
        return null
    }

    return (
        <>
            <FormField
                fullWidth
                name={`channel_products.${cpIndex}.channel_stock_units.${csuIndex}.price`}
                type="number"
                placeholder={gettext('Price')}
                disabled={isDisabled}
                helperText={<>&nbsp;</>}
                className={classes.rowPriceField}
                InputProps={{ className: classes.priceField }}
                inputProps={{ min: 0 }}
                rules={{
                    validate: {
                        validatePrices: (value, form) => {
                            if (isDisabled) {
                                return true
                            }

                            if (typeof value !== 'string') {
                                return gettext('Invalid input')
                            }

                            return channelValidator.validateChannelStockUnitPrice(
                                value,
                                form.channel_products[cpIndex]
                            )
                        },
                    },
                    deps: nonEmptyDependencies,
                }}
            />
        </>
    )
}

function ProductPricingSkuColumn(props: { suIndex: number; fieldWidth: number }) {
    const { suIndex, fieldWidth } = props
    const { control } = useProductFormContext()
    const sku = useWatch({ control, name: `stock_units.${suIndex}.sku`, exact: true })
    const classes = usePricingStyles({ fieldWidth })

    return (
        <Typography className={classNames(classes.skuColumn, !sku && classes.skuPlaceholder)}>
            {sku || `${gettext('Stock unit')} ${suIndex + 1}`}
        </Typography>
    )
}

function ProductDimensionOptionColumn(props: {
    dimensionIndex: number
    optionId: string | number
}) {
    const { dimensionIndex, optionId } = props
    const { getDimensionOptionName } = useDimensionHelpers()
    const optionName = getDimensionOptionName(dimensionIndex, optionId)

    return (
        <td>
            <Typography>{optionName}</Typography>
        </td>
    )
}

type Props = {
    channelProducts: ProductFormSchema['channel_products']
    channelProductIndexById: Record<string, number>
    stockUnits: ProductFormSchema['stock_units']
    dimensions: ProductFormSchema['dimensions']
    fieldWidth?: number
    onSetPriceForShop: (shopId: number, value: number) => void
    onSetPriceForStockUnits: (stockUnitIndex: number, value: number) => void
}

const ITEM_HEIGHT = 72

const components: TableComponents<StockUnitFormSchema> = {
    Table: (props) => (
        <table
            {...props}
            style={{
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                borderSpacing: '10px 0',
            }}
        />
    ),
    TableRow: (props) => <TableRow {...props} style={{ verticalAlign: 'initial' }} />,
    TableBody,
    TableHead,
    TableFoot: TableFooter,
}
export default memo(function PricingTable({
    channelProducts,
    stockUnits,
    dimensions,
    fieldWidth = DEFAULT_FIELD_WIDTH,
    channelProductIndexById,
    onSetPriceForShop,
    onSetPriceForStockUnits,
}: Props) {
    const classes = usePricingStyles({ fieldWidth })
    const hasMultipleChannels = channelProducts.length > 1
    const [containerHeight, setContainerHeight] = useState(0)
    const ref = useVirtualizationContainer('pricing')

    const tableStyles = useMemo(() => ({ height: containerHeight }), [containerHeight])

    const dimNames = useMemo(
        () => (dimensions ? dimensions.map((dim) => Object.values(dim.translations)[0].name) : []),
        [dimensions]
    )

    const fixedHeaderContent = useCallback(() => {
        return (
            <>
                <tr className={classes.headerRow}>
                    {hasMultipleChannels && <th scope="col" />}
                    <th scope="col" />

                    {dimNames.map((dimName) => (
                        <th scope="col" key={dimName} />
                    ))}

                    {hasMultipleChannels &&
                        channelProducts.map((cp, cpIndex) => (
                            <th
                                key={cp.id || cpIndex}
                                scope="col"
                                className={classes.priceFieldColumn}
                            >
                                <div>
                                    <TextField
                                        type="number"
                                        placeholder={gettext('Set all')}
                                        variant="outlined"
                                        margin="dense"
                                        InputProps={{ className: classes.priceField }}
                                        disabled={cp.is_promotion_period}
                                        inputProps={{ min: 0 }}
                                        onChange={(e) => {
                                            onSetPriceForShop(
                                                cp.shop.id,
                                                parseFloat(e.target.value)
                                            )
                                        }}
                                    />
                                    <KeyboardArrowDownIcon className={classes.directionIcon} />
                                </div>
                            </th>
                        ))}
                </tr>
                <tr className={classes.headerRow}>
                    {hasMultipleChannels && <th scope="col" />}
                    <th scope="col">{gettext('SKU')}</th>
                    {dimNames.map((dimName) => (
                        <th scope="col" key={dimName}>
                            {dimName}
                        </th>
                    ))}
                    {channelProducts.map((cp, cpIndex) => (
                        <th key={cp.id || cpIndex} scope="col" className={classes.priceColumn}>
                            <div className={classes.pricingColumnHeader}>
                                <img
                                    alt={cp.shop.channel.name}
                                    className={classes.pricingChannelIconURL}
                                    src={cp.shop.channel.channel_square_icon_url}
                                />
                                <Typography className={classes.pricingColumnShopName}>
                                    {cp.shop.shop_name}
                                </Typography>
                            </div>
                        </th>
                    ))}
                </tr>
            </>
        )
    }, [
        channelProducts,
        classes.directionIcon,
        classes.headerRow,
        classes.priceColumn,
        classes.priceField,
        classes.priceFieldColumn,
        classes.pricingChannelIconURL,
        classes.pricingColumnHeader,
        classes.pricingColumnShopName,
        hasMultipleChannels,
        dimNames,
        onSetPriceForShop,
    ])

    const itemContent = useCallback(
        (index: number, stockUnit: ProductFormSchema['stock_units'][number]) => (
            <>
                {channelProducts.length > 1 && (
                    <td>
                        <div className={classes.firstColumn}>
                            <TextField
                                type="number"
                                variant="outlined"
                                margin="dense"
                                placeholder={gettext('Set all')}
                                InputProps={{ className: classes.priceField }}
                                inputProps={{ min: 0 }}
                                onChange={(e) => {
                                    onSetPriceForStockUnits(index, parseFloat(e.target.value))
                                }}
                            />
                            <KeyboardArrowRightIcon className={classes.directionIcon} />
                        </div>
                    </td>
                )}

                <td>
                    <ProductPricingSkuColumn suIndex={index} fieldWidth={fieldWidth} />
                </td>

                {stockUnit.dimension_options?.map((option, index) => (
                    <ProductDimensionOptionColumn
                        key={index}
                        dimensionIndex={option.product_dimension.index}
                        optionId={option.id}
                    />
                ))}

                {channelProducts.map((cp) => {
                    const channelProductIndex = channelProductIndexById[cp.id]

                    return (
                        <td key={cp.id} className={classes.priceColumn}>
                            <div>
                                <PriceField
                                    fieldWidth={fieldWidth}
                                    csuIndex={index}
                                    cpIndex={channelProductIndex}
                                    channelStockUnits={cp.channel_stock_units}
                                />
                            </div>
                        </td>
                    )
                })}
            </>
        ),
        [
            channelProductIndexById,
            channelProducts,
            fieldWidth,
            classes.directionIcon,
            classes.firstColumn,
            classes.priceColumn,
            classes.priceField,
            onSetPriceForStockUnits,
        ]
    )

    /**
     * Adjust the height of the container to fit the content
     * The offset acts as a buffer to prevent the scrollbar from appearing when it's not needed
     */
    const handleTotalListHeightChange = useCallback(
        (height: number) => {
            const OFFSET = 10

            setContainerHeight((prev) => {
                if (height + OFFSET === prev) {
                    return prev
                }

                const allRowsHeight = stockUnits.length * ITEM_HEIGHT
                const tableHeight = allRowsHeight + height
                const nextHeight = Math.min(tableHeight + OFFSET, MAX_VIRTUALIZED_HEIGHT)

                return nextHeight
            })
        },
        [stockUnits.length]
    )

    return (
        <TableVirtuoso
            ref={ref}
            data={stockUnits}
            components={components}
            style={tableStyles}
            fixedItemHeight={ITEM_HEIGHT}
            totalListHeightChanged={handleTotalListHeightChange}
            fixedHeaderContent={fixedHeaderContent}
            itemContent={itemContent}
            computeItemKey={computeStockUnitItemKey}
        />
    )
})
